import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
    DataMartField,
    DefinedField,
    SalesforceFieldMetadata,
    SalesforceListFieldResponse,
} from '@r1-compass-library/models';
import { DivisionDataMappingCompatibleTypesService } from './division-data-mapping-types.service';
import { FieldMappingForm } from './form.model';

@Injectable({
    providedIn: 'root',
})
export class DivisionDataMappingFormValidatorsService {
    readonly #divisionDataMappingComatibleTypesService = inject(DivisionDataMappingCompatibleTypesService);

    public fieldMappingTypeValidator(type: 'play' | 'rec'): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.parent instanceof FormGroup) {
                const typeSelectedCtrl = (control.parent as FormGroup<FieldMappingForm>)['controls']['mappingType'];

                if (
                    (typeSelectedCtrl.getRawValue() === 'play' && type === 'play') ||
                    (typeSelectedCtrl.getRawValue() === 'rec' && type === 'rec') ||
                    typeSelectedCtrl.getRawValue() === 'both'
                ) {
                    if (!control.getRawValue()) {
                        return { req: true };
                    } else {
                        return null;
                    }
                }

                return null;
            } else {
                return null;
            }
        };
    }

    public mappedFieldDataTypeMisMatchValidator(
        dataProvider: () => {
            dataMartFields: DataMartField[];
            playSalesforceFields: SalesforceFieldMetadata[];
            recommendationSalesforceFields: SalesforceFieldMetadata[];
            definedFields: DefinedField[];
        }
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.parent instanceof FormGroup) {
                const parentCtrl = control.parent as FormGroup<FieldMappingForm>;

                const sourceControl = parentCtrl.controls['sourceField'];
                const targetPlayControl = parentCtrl.controls['targetPlayField'];
                const targetRecControl = parentCtrl.controls['targetRecommendationField'];

                if (parentCtrl.controls['fieldType'].getRawValue() === 'predefinedField') {
                    return null; // Skip if just a predefinedField
                }

                // Get fresh data each time the validator runs
                const { dataMartFields, playSalesforceFields, recommendationSalesforceFields, definedFields } =
                    dataProvider();

                if (
                    (dataMartFields.length === 0 && definedFields.length === 0) ||
                    (playSalesforceFields.length === 0 && recommendationSalesforceFields.length === 0)
                ) {
                    return null; // Skip if we do not have the necessary fields
                }

                const dataMartField = dataMartFields.find(f => f.name === sourceControl.getRawValue());

                const definedField = definedFields.find(f => f.id === sourceControl.getRawValue());
                const targetPlayField = playSalesforceFields.find(f => f.name === targetPlayControl.getRawValue());
                const targetRecField = recommendationSalesforceFields.find(
                    f => f.name === targetRecControl.getRawValue()
                );

                // Track if any mismatch is found
                let hasMismatch = false;

                // If sourceField is populated, validate against both target fields
                if (dataMartField) {
                    const compatibleTypes = this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes(
                        dataMartField.dataType
                    );

                    if (targetPlayField && !compatibleTypes.includes(targetPlayField.type.toLowerCase())) {
                        hasMismatch = true;
                    }

                    if (targetRecField && !compatibleTypes.includes(targetRecField.type.toLowerCase())) {
                        hasMismatch = true;
                    }
                }

                // If sourceField is a defined field, validate against both target fields
                if (definedField) {
                    // Map defined field types to compatible Salesforce types
                    let compatibleTypes: string[] = [];

                    if (definedField.type === 'varchar' || definedField.type === 'picklist') {
                        compatibleTypes =
                            this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes('varchar');
                    } else if (definedField.type === 'date') {
                        compatibleTypes =
                            this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes('date');
                    } else if (definedField.type === 'boolean') {
                        compatibleTypes =
                            this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes('boolean');
                    }

                    if (targetPlayField && !compatibleTypes.includes(targetPlayField.type.toLowerCase())) {
                        hasMismatch = true;
                    }

                    if (targetRecField && !compatibleTypes.includes(targetRecField.type.toLowerCase())) {
                        hasMismatch = true;
                    }
                }

                // If targetPlayField is populated, validate against sourceField and targetRecField
                if (targetPlayField) {
                    const salesforcePlayTypes = this.#divisionDataMappingComatibleTypesService.getSalesforceTypes(
                        targetPlayField.type
                    );

                    // Validate against sourceField (DataMart field)
                    if (dataMartField) {
                        const compatibleDataMartTypes =
                            this.#divisionDataMappingComatibleTypesService.getCompatibleDataMartTypes(
                                targetPlayField.type
                            );
                        if (!compatibleDataMartTypes.includes(dataMartField.dataType.toLowerCase())) {
                            hasMismatch = true;
                        }
                    }

                    // Validate against sourceField (Defined field)
                    if (definedField) {
                        // Get compatible defined field types for the Salesforce field type
                        const compatibleDefinedTypes: string[] =
                            this.#divisionDataMappingComatibleTypesService.getCompatibleDataMartTypes(
                                targetPlayField.type
                            );
                        if (definedField.type === 'varchar' || definedField.type === 'picklist') {
                            // Check if varchar/text types are compatible
                            if (!compatibleDefinedTypes.includes('varchar')) {
                                hasMismatch = true;
                            }
                        } else if (definedField.type === 'date') {
                            // Check if date types are compatible
                            if (!compatibleDefinedTypes.includes('date')) {
                                hasMismatch = true;
                            }
                        } else if (definedField.type === 'boolean') {
                            // Check if boolean types are compatible
                            if (!compatibleDefinedTypes.includes('boolean')) {
                                hasMismatch = true;
                            }
                        }
                    }

                    // Validate against targetRecField
                    if (targetRecField && !salesforcePlayTypes.includes(targetRecField.type.toLowerCase())) {
                        hasMismatch = true;
                    }
                }

                // If targetRecField is populated, validate against sourceField and targetPlayField
                if (targetRecField) {
                    // Validate against sourceField (DataMart field)
                    if (dataMartField) {
                        const compatibleDataMartTypes =
                            this.#divisionDataMappingComatibleTypesService.getCompatibleDataMartTypes(
                                targetRecField.type
                            );
                        if (!compatibleDataMartTypes.includes(dataMartField.dataType.toLowerCase())) {
                            hasMismatch = true;
                        }
                    }

                    // Validate against sourceField (Defined field)
                    if (definedField) {
                        // Get compatible defined field types for the Salesforce field type
                        const compatibleDefinedTypes: string[] =
                            this.#divisionDataMappingComatibleTypesService.getCompatibleDataMartTypes(
                                targetRecField.type
                            );
                        if (definedField.type === 'varchar' || definedField.type === 'picklist') {
                            // Check if varchar/text types are compatible
                            if (!compatibleDefinedTypes.includes('varchar')) {
                                hasMismatch = true;
                            }
                        } else if (definedField.type === 'date') {
                            // Check if date types are compatible
                            if (!compatibleDefinedTypes.includes('date')) {
                                hasMismatch = true;
                            }
                        } else if (definedField.type === 'boolean') {
                            // Check if boolean types are compatible
                            if (!compatibleDefinedTypes.includes('boolean')) {
                                hasMismatch = true;
                            }
                        }
                    }

                    // Validate against targetPlayField (already checked in targetPlayField section)
                    // No need to check again to avoid duplicate validation
                }

                if (hasMismatch) {
                    return { dataTypeMismatch: true };
                }

                // Avoid cross-triggering sibling validations here to prevent recursive update loops.
                // The component-level (selectionChange) handlers will explicitly revalidate sibling controls
                // after the current control’s value has settled.
                return null;
            } else {
                return null;
            }
        };
    }

    /**
     * @description Triggers validation when data becomes available.
     * This resolves the race condition where validators return null because data hasn't loaded yet.
     * When data loads, we need to explicitly trigger validation so validators can re-evaluate with the loaded data.
     */
    public triggerValidationOnMappingRows(
        dataMartFields: DataMartField[],
        playFieldsOptions: SalesforceListFieldResponse | undefined,
        recommendationFieldsOptions: SalesforceListFieldResponse | undefined,
        fieldMappings: FormArray<FormGroup<FieldMappingForm>>
    ) {
        // If we have any data available, trigger validation on all field mapping rows
        if (
            dataMartFields.length > 0 ||
            playFieldsOptions?.fields?.length ||
            recommendationFieldsOptions?.fields?.length
        ) {
            // Trigger validation on all field mapping form controls
            for (let i = 0; i < fieldMappings.length; i++) {
                const row = fieldMappings.at(i);

                // Only update validation if the controls have values
                if (
                    row.controls.sourceField.getRawValue() ||
                    row.controls.targetPlayField.getRawValue() ||
                    row.controls.targetRecommendationField.getRawValue()
                ) {
                    // Mark controls as touched so validation errors will display in the UI
                    row.controls.sourceField.markAllAsTouched();
                    row.controls.targetPlayField.markAllAsTouched();
                    row.controls.targetRecommendationField.markAllAsTouched();

                    // Update validation with emitEvent: true to trigger change detection
                    row.controls.sourceField.updateValueAndValidity({ emitEvent: true });
                    row.controls.targetPlayField.updateValueAndValidity({ emitEvent: true });
                    row.controls.targetRecommendationField.updateValueAndValidity({ emitEvent: true });
                }
            }
        }
    }
}
